<script setup lang="ts">
import { ref, computed, watch, Ref, inject, onBeforeMount, onBeforeUnmount, onMounted, nextTick, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useStore } from 'vuex';
import { useLocalization } from '@/plugins/localization';
import Pager from '@/helpers/Pager';
import { kebabCase, isEqual } from 'lodash';
import { KeyValuePair } from '@/helpers/Interfaces';
import { Definition as ContractorDefinition } from '../logito-contractor';
import { HasLabel } from '@/components/builder/form/traits/HasLabel';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { FormEntry } from '../form';
import { RelatedDocumentType, RelatedDocumentEntry, instaceOfRelatedDocumentEntry, Definition as RelatedDocumentDefinition } from '.';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import FormsService, { Contractor, ModuleOption } from '@/modules/studio/forms/services/FormsService';
import AutocompleteService from '@/modules/core/common/services/AutocompleteService';
import GridService, { IMetaModel } from '@/modules/core/common/services/GridService';
import DocumentsListModal from './modals/DocumentsListModal.vue';
import properties from '../../properties';
import { useRoute } from 'vue-router';
import { Definition as LogitoRelatedDocumentsDefinition } from '@/components/forms/blueprints/logito-related-documents';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { moduleBaseUrlCache } from './moduleCache';

import ModulesService from '@/modules/low-code/services/ModulesService';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'related-document-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $events } = useEvents();
const { $t } = useLocalization();
const store = useStore();
const level = inject<number>('level', 0);

const entryData = ref(new RelatedDocumentEntry()) as Ref<RelatedDocumentEntry>;
const documentsListModalRef = ref<InstanceType<typeof DocumentsListModal | null>>(null);
const reloadKey = ref(0);
const viewPermissions = ref<Record<string, IMetaModel>>({});
const selectedModules = ref<ModuleOption[]>([]);
const moduleOptions = ref<ModuleOption[]>([]);
const options = ref<Record<string, any>[]>([]);
const moduleBaseUrl = ref<string | null>(null);
const filters = ref<any>(null);
const fieldOptions = ref<Record<string, string>[]>([]);
const filterOptions = ref<Record<string, string>[]>([]);
const importFilterField = ref<KeyValuePair | null>(null);
const relatedDocumentsFieldReloadKey = ref(0);
const relatedDocsFieldOptions = ref<Record<string, string>[]>([]);
const fieldRelatedTitleTemplateOptions = ref<Record<string, string>[]>([]);

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

let lastEmitTime = 0;

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instaceOfRelatedDocumentEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }

    $events.$on(`update-related-documents-${level ?? 0}`, async () =>
    {
        if (!blueprint.value.multiselect && value.value) await onDocumentChanged(value.value);
    });

    $events.$on('reload-related-documents', async () =>
    {
        await reloadData();
    });

    if ((props.parent?.type ?? '') === 'table')
    {
        $events.$on(`reload-related-documents-${props.parent.name}-${props.index}-${props.blueprint.name}`, async () =>
        {
            await reloadData();
        });
    }
});

const reloadData = async () =>
{
    await fetchOptionsSelect();

    const option = options.value.find((p) => p.key == (value.value as KeyValuePair)?.key);

    if (!option) return;

    onDocumentChanged(option);
};

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        $events.$off('reload-related-documents');
        $events.$off(`update-related-documents-${level ?? 0}`);

        if ((props.parent?.type ?? '') === 'table')
            $events.$off(`reload-related-documents-${props.parent.name}-${props.index}-${props.blueprint.name}`);

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const relatedDocumentFilter = computed(() =>
{
    const dataSource = props.blueprint.dataSource;

    if (!dataSource) return null;

    const parsedDataSource = JSON.parse(dataSource);

    if (!parsedDataSource && !parsedDataSource['relatedDocumentsFilter']) return null;

    const relatedDocumentsFilter = parsedDataSource['relatedDocumentsFilter'];

    return relatedDocumentsFilter;
});

const relatedDocumentFilterValue = computed(() =>
{
    if (!relatedDocumentFilter.value) return null;

    const fieldName = relatedDocumentFilter.value;
    const blueprint = props.form.schema.find(fieldName) as EntryFactory<any>;
    const parent = props.form.schema.parent(blueprint);

    if (!blueprint) return null;

    const element = parent?.type === 'table'
        ? props.entry[parent.name].data[props.index][fieldName]
        : props.entry[fieldName];

    if (element && 'getValues' in element)
    {
        return element.getValues(blueprint, props.form);
    }

    return null;
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        if (!blueprint.value.multiselect)
        {
            return value?.[0] ?? null;
        }

        return value;
    },
    set(value: KeyValuePair[] | KeyValuePair | null)
    {
        if (Array.isArray(value)) entryData.value.data = value;
        else if (value != null) entryData.value.data = [value];
        else entryData.value.data = null;

        const now = Date.now();

        if (now - lastEmitTime < 50) return; // zabezpieczenie przed duplikatem

        lastEmitTime = now;

        useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    },
});

const blueprint = computed(() => props.blueprint);
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const design = computed(() => props.form.designMode());
const route = useRoute();

const isExternalForm = computed(() => route.meta.externalAccess === true);
const isInTable = computed(() => props.parent?.type === 'table');

const buildUrlWithFilter = computed(() =>
{
    if (design.value || isExternalForm.value || !relatedDocumentFilterValue.value || !relatedDocumentFilter.value)
    {
        return null;
    }

    const baseUrl = `simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${blueprint.value.name}`;
    const filterParam = `${relatedDocumentFilter.value}=${relatedDocumentFilterValue.value}`;

    const paramExists = baseUrl.includes(`?${filterParam}`) || baseUrl.includes(`&${filterParam}`);

    if (paramExists)
    {
        return baseUrl;
    }

    const hasExistingParams = baseUrl.includes('?');
    const separator = hasExistingParams ? '&' : '?';

    return `${baseUrl}${separator}${filterParam}`;
});

const endpoint = computed(() =>
    !design.value
        ? isExternalForm.value
            ? `external-form/${props.entry.module}/${props.entry.formId}/select-options/${props.entry.actionName}/${blueprint.value.name}`
            : buildUrlWithFilter.value ?? `simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${blueprint.value.name}`
        : null
);

const scope = computed(() =>
{
    const result: Record<string, string> = {};

    if (design.value) return result;

    if (blueprint.value.hasFilterByContractor)
    {
        result[blueprint.value.contractorFilter.key] = (
            props.entry[blueprint.value.contractorFilter.key]?.data as KeyValuePair[]
        )?.first()?.key;
    }

    if (blueprint.value.hasFilterByRelatedDocuments)
    {
        const relFieldName = blueprint.value.relatedDocumentsFilter;
        const bp = props.form.schema.find(relFieldName) as EntryFactory<any>;
        const parent = props.form.schema.parent(bp);

        const element = parent?.type === 'table' ? props.entry[parent.name].data[props.index][relFieldName] : props.entry[relFieldName];

        result[relFieldName] = (element?.data as KeyValuePair[])?.first()?.key;
    }

    const filterMappings = blueprint.value.filterMappings ?? {};

    if (blueprint.value.modules?.length != 1 || !Object.keys(filterMappings).length) return result;

    for (const item in blueprint.value.filterMappings)
    {
        const blueprint = props.form.schema.find(item) as EntryFactory<any>;
        const parent = props.form.schema.parent(blueprint);

        if (!blueprint) continue;

        const element = parent?.type === 'table' ? props.entry[parent.name].data[props.index][item] : props.entry[item];

        if (element && 'getValues' in element)
        {
            result[item] = element.getValues(blueprint, props.form);
        }
    }

    return result;
});

const isActionButton = computed(() => Object.entries(viewPermissions.value ?? {}).length && !readonly.value && props.blueprint.showActionButton);
const isModelButton = computed(() => !readonly.value);
const isFilters = computed(() => selectedModules.value?.length == 1);

const contractorComponents = computed(() =>
    props.form.schema.components(ContractorDefinition.type).map((p) => ({
        key: p.name,
        value: `${props.form.localization.translate((p as HasLabel).label)} (${p.name})`,
    }))
);

const contractorFilter = computed(() => ({
    key: contractorComponents.value.find((p) => p.key == blueprint.value.contractorFilter.key) ?? null,
    value: fieldOptions.value.find((p) => p.key == blueprint.value.contractorFilter.value) ?? null,
}));


const relatedDocumentComponents = computed(() =>
    props.form.schema.components(RelatedDocumentDefinition.type, blueprint.value).map((p) => ({
        key: p.name,
        value: `${props.form.localization.translate((p as HasLabel).label)} (${p.name})`,
    }))
);

const relatedDocumentsFilter = computed(() =>
    relatedDocumentComponents.value.find((p) => p.key == blueprint.value.relatedDocumentsFilter) ?? null
);

const relatedDocumentsFieldFilter = computed(() =>
    relatedDocsFieldOptions.value.find((p) => p.key == blueprint.value.relatedDocumentsFieldFilter) ?? null
);

const updateContractorFilter = (type: string, value: KeyValuePair): void =>
{
    if (type == 'key') blueprint.value.contractorFilter.key = value?.key ?? null;
    else if (type == 'value') blueprint.value.contractorFilter.value = value?.key ?? null;
};

const updateRelatedDocumentsFilter = (value: KeyValuePair): void =>
{
    blueprint.value.relatedDocumentsFilter = value?.key ?? null;
};

const updateRelatedDocumentsFieldFilter = (value: KeyValuePair): void =>
{
    blueprint.value.relatedDocumentsFieldFilter = value?.key ?? null;
};

const isContractors = (contractor: Array<Contractor>) => contractor?.length > 0;
const contractorText = (contractor: Array<Contractor>) =>
    `${$t('[[[Kontrahent]]]')}: ${contractor.map((item: Contractor) => item.name).join(', ')}`;

const openModal = () =>
{
    store.commit('common/grid/SET_INITIALIZATION', false);
    documentsListModalRef.value.showModal();
};

const fetchModuleOptions = async () =>
{
    const response = (await FormsService.getModuleOptions(new Pager(1, 999))).items.map((p) => p.result);

    moduleOptions.value = response;
};

const fetchOptionsSelect = async () =>
{
    if (design.value) return;

    const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
        endpoint.value,
        filters.value,
        new Pager(1, 999, '', 'ASC')
    );

    if (!response) return;

    options.value = response.items.map((item) => item.result);
};

const fetchModuleBaseUrl = async () =>
{
    if (blueprint.value.modules?.length !== 1)
    {
        moduleBaseUrl.value = null;
        viewPermissions.value = {};

        return;
    }

    const moduleId = Number(blueprint.value.modules[0]);

    if (isInTable.value)
    {
        if (!moduleBaseUrlCache.has(moduleId))
        {
            const fetchPromise = (async () =>
            {
                const baseUrl = await FormsService.getModuleBaseUrl(moduleId);

                if (!baseUrl || isExternalForm.value)
                {
                    return { baseUrl: null, permissions: {} };
                }

                const perms = await GridService.fetchActions(baseUrl);

                return { baseUrl, permissions: perms ?? {} };
            })();

            moduleBaseUrlCache.set(moduleId, fetchPromise);
        }

        const cached = await moduleBaseUrlCache.get(moduleId);

        await nextTick();

        moduleBaseUrl.value = cached.baseUrl;
        viewPermissions.value = cached.permissions;
    }
    else
    {
        const baseUrl = await FormsService.getModuleBaseUrl(moduleId);

        if (!baseUrl || isExternalForm.value)
        {
            moduleBaseUrl.value = null;
            viewPermissions.value = {};

            return;
        }

        const perms = await GridService.fetchActions(baseUrl);

        await nextTick();

        moduleBaseUrl.value = baseUrl;
        viewPermissions.value = perms ?? {};
    }
};

const fetchValueOptions = async () =>
{
    const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
        `admin/forms/module-fields/${kebabCase(selectedModules.value[0]?.key)}`,
        null,
        new Pager(1, 999, '', 'ASC')
    );

    if (!response) return;

    fieldOptions.value = response.items
        .map((item) => item.result)
        .map((p) => ({ ...p, value: `${p.value} (${p.key})` }));
};

const fetchFilterOptions = async () =>
{
    const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
        `admin/forms/module-fields/${kebabCase(selectedModules.value[0]?.key)}`,
        null,
        new Pager(1, 999, '', 'ASC'),
        null, null,
        { filterOnly: true }
    );


    if (!response) return;

    filterOptions.value = response.items
        .map((item) => item.result)
        .map((p) => ({ ...p, value: `${p.value} (${p.key})` }));
};

const fetchRelatedFieldOptions = async () =>
{
    let result = [] as any[];

    if (blueprint.value.hasFilterByRelatedDocuments)
    {
        const relFieldName = blueprint.value.relatedDocumentsFilter;
        const bp = props.form.schema.find(relFieldName) as EntryFactory<any>;
        const modules = bp.modules;

        if (modules.length == 1)
        {
            const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
                `admin/forms/module-fields/${modules[0]}`,
                null,
                new Pager(1, 999, '', 'ASC')
            );

            if (!response) return;

            result = response.items
                .map((item) => item.result)
                .map((p) => ({ ...p, value: `${p.value} (${p.key})` }))
                .filter((p) => p.type.toLowerCase() === `${LogitoRelatedDocumentsDefinition.type.replace('-', '')}Type`.toLowerCase());
        }
    }

    relatedDocsFieldOptions.value = result;
    relatedDocumentsFieldReloadKey.value++;
};

const fetchRelatedTitleTemplateOptions = async () =>
{
    if (selectedModules.value.length == 0)
    {
        fieldRelatedTitleTemplateOptions.value = [];

        return;
    }

    if (selectedModules.value.length == 1)
    {
        const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
            `admin/forms/module-fields/${kebabCase(selectedModules.value[0]?.key)}`,
            null,
            new Pager(1, 999, '', 'ASC')
        );

        if (!response)
        {
            fieldRelatedTitleTemplateOptions.value = [];

            return;
        }

        fieldRelatedTitleTemplateOptions.value = response.items
            .map((item) => item.result)
            .map((p) => ({ ...p, value: `${p.value} (${p.key})` }));
    }

    if (selectedModules.value.length > 1)
    {
        const result = [] as any[];
        const response = await ModulesService.gridConfig();

        Object.entries(response).map(([key, value]) =>
        {
            result.push({
                key: 'SharedField_' + key,
                value: value.headerName,
                type: 'SharedField'
            });
        });

        fieldRelatedTitleTemplateOptions.value = result;
    }
};

const onDocumentChanged = (option: any) =>
{
    if (blueprint.value.modules?.length != 1 || !Object.keys(blueprint.value.fieldMappings ?? {}).length || option == null) return;

    for (const item in blueprint.value.fieldMappings)
    {
        const blueprint = props.form.schema.find(item) as EntryFactory<any>;
        const parent = props.form.schema.parent(blueprint);

        if (!blueprint) continue;

        const data = option.additionalFields?.[props.blueprint.fieldMappings?.[item]]?.data;

        if (parent?.type === 'table')
        {
            // TODO: Do not mutate props
            props.entry[parent.name].data[props.index][blueprint.name] = blueprint.createEntry(data); // eslint-disable-line
            useControlReload($events, blueprint.name, level, parent.name, props.index).emitReload(props.index);

            continue;
        }

        // TODO: Do not mutate props
        props.entry[item] = blueprint.updateEntry(data, props.entry[item]); // eslint-disable-line
        useControlReload($events, item, level).emitReload();
    }

    //$events.$emit('form-reload');
};

const onModalDocumentSelected = async (publicIdData: string | string[]) =>
{
    if (Array.isArray(publicIdData))
    {
        const responses = await Promise.all(
            publicIdData.map(async (id) =>
            {
                const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
                    endpoint.value,
                    null,
                    new Pager(1),
                    { key: id }
                );

                return response.items[0]?.result;
            })
        );

        value.value = [];

        responses.forEach((result) =>
        {
            if (result)
            {
                (value.value as KeyValuePair[]).push(result as any);
            }
        });
    }
    else
    {
        const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
            endpoint.value,
            null,
            new Pager(1),
            { key: publicIdData }
        );

        if (props.blueprint.multiselect)
        {
            if (!value.value) value.value = [];

            (value.value as KeyValuePair[]).push(response.items[0].result as any);
        }
        else
        {
            value.value = response.items[0].result as any;
        }
    }

    onDocumentChanged(value.value);
};

const init = async () =>
{
    if (design.value)
    {
        await fetchModuleOptions();

        selectedModules.value = blueprint.value.modules?.map?.((p) => moduleOptions.value.find((x) => x.key == p));
        filters.value = blueprint.value.filtersJson ? JSON.parse(blueprint.value.filtersJson) : null;

        if (selectedModules.value?.length == 1)
        {
            await fetchValueOptions();
            await fetchRelatedFieldOptions();
            await fetchFilterOptions();
        }

        if (selectedModules.value?.length == 1) await fetchRelatedTitleTemplateOptions();

        importFilterField.value = fieldOptions.value.find((p) => p.key == blueprint.value.importFilterField) as any;
    }

    await fetchModuleBaseUrl();

    relatedDocumentFilterValue.value;
};

watch(() => blueprint.value.modules, () => fetchModuleBaseUrl());
watch(() => importFilterField.value, () => blueprint.value.importFilterField = importFilterField.value?.key);
watch(() => filters.value, () => blueprint.value.filtersJson = filters.value ? JSON.stringify(filters.value) : null, { deep: true });
watch(() => selectedModules.value, async (newValue: any[]) =>
{
    blueprint.value.modules = newValue.map((p) => p.key);

    if (newValue.length == 1)
    {
        if (blueprint.value.contractorFilter.value == 'Contractor')
            blueprint.value.contractorFilter.value = null;

        await fetchValueOptions();
        await fetchFilterOptions();
        await fetchRelatedFieldOptions();
    }
    else updateContractorFilter('value', { key: 'Contractor', value: $t('[[[Kontrahent]]]') });

    await fetchRelatedTitleTemplateOptions();
});

watch(() => scope.value, (newValue, oldValue) =>
{
    if (!isEqual(newValue, oldValue)) value.value = null;
});

watch(value, () =>
{
    relatedDocumentFilterValue.value;
}, { deep: true });

onMounted(async () =>
{
    await init();
});
</script>

<template>
    <form-component-wrapper class="relatedDocument-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="d-flex gap-1">
                    <div class="d-flex flex-fill scroll scroll-x">
                        <ideo-select
                            v-model="value"
                            :disabled="readonly"
                            :id="blueprint.id"
                            :placeholder="placeholder"
                            :endpoint="endpoint"
                            :multiselect="blueprint.multiselect"
                            :deselect="isActionButton"
                            :clear-button="!isActionButton"
                            :narrow-options="scope"
                            :custom-classes="{ 'border-radius-right-none': isActionButton }"
                            :do-not-show-tooltip="false"
                            class="flex-fill logito-related-doc"
                            @update:modelValue="onDocumentChanged"
                        >
                            <template #option="{ option }">
                                <div class="d-flex flex-column">
                                    <strong v-if="option.value">{{ option.value }}</strong>
                                    <template v-for="(templateField, fieldIndex) in option.titleTemplateFields" :key="`field_index_${fieldIndex}`">
                                        <small><b>{{ templateField.key }}:</b> {{ templateField.value }}</small>
                                    </template>
                                </div>
                            </template>
                        </ideo-select>
                        <view-action-button
                            v-if="isActionButton"
                            :permissions="viewPermissions"
                            :licence="moduleBaseUrl"
                            :custom-classes="{ 'border-radius-left-none m-0': isActionButton }"
                            :only-icons="true"
                            :is-modal-mode="true"
                            :level="level ?? 0"
                            :blueprint="blueprint"
                            :parent="parent"
                            :index="index"
                        />
                    </div>
                    <ideo-button v-if="isModelButton" variant="secondary" icon="fa-solid fa-search" class="m-0" @click="openModal" />
                </div>
                <form-error-message :entry="entryData" name="values" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
            <documents-list-modal
                ref="documentsListModalRef"
                :blueprint="blueprint"
                :entry="entry"
                :form="form"
                :index="index"
                :module-licence="moduleBaseUrl"
                :key="reloadKey"
                :selected-ids="value"
                @hide="reloadKey++"
                @document-selected="onModalDocumentSelected"
            />
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-checkbox v-model="blueprint.multiselect" :label="$t('[[[Wielokrotny wybór]]]')" />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <ideo-form-group :label="$t('[[[Moduły]]]')">
                        <template #default>
                            <ideo-select v-model="selectedModules" :options="moduleOptions" :multiselect="true" />
                        </template>
                        <template #description>
                            {{ $t('[[[Wybierz moduł aby ograniczyć wiązanie do wybranego typu dokumentu]]]') }}
                        </template>
                    </ideo-form-group>
                    <field-related-title-template
                        v-model="blueprint.titleTemplateFields"
                        :label="$t('[[[Pola wyświetlane]]]')"
                        :form="form"
                        :blueprint="blueprint"
                        :options="fieldRelatedTitleTemplateOptions"
                        :selected-modules="selectedModules"
                    />
                    <field-checkbox
                        v-model="blueprint.showActionButton"
                        :label="$t('[[[Wyświetlaj przycisk akcji]]]')"
                    />
                    <field-checkbox
                        v-model="blueprint.onlyUnfinished"
                        :label="$t('[[[Pozwalaj wiązać tylko niezakończone dokumenty]]]')"
                    />
                    <field-checkbox
                        v-model="blueprint.lockOnAction"
                        :label="$t('[[[Blokuj akcje na wybranym dokumencie podczas wykonania akcji grupowych]]]')"
                    />
                    <ideo-form-group :label="$t('[[[Wyświetlaj tylko dokumenty których użytkownik:]]]')">
                        <ideo-form-checkbox v-model="blueprint.allowAuthor">
                            {{ $t('[[[Jest autorem]]]') }}
                        </ideo-form-checkbox>
                        <ideo-form-checkbox v-model="blueprint.allowTargets">
                            {{ $t('[[[Realizuje obecnie zadanie w obiegu dokumentu]]]') }}
                        </ideo-form-checkbox>
                        <ideo-form-checkbox v-model="blueprint.allowAll">
                            {{ $t('[[[Ma dostęp do dokumentu]]]') }}
                        </ideo-form-checkbox>
                    </ideo-form-group>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-error :form="form" :blueprint="blueprint" :label="$t('[[[Niestandardowy błąd]]]')" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Filtry]]]')">
                    <field-filters
                        v-if="isFilters"
                        v-model="filters"
                        :selected-modules="selectedModules"
                        :label="$t('[[[Filtry]]]')"
                    />
                    <field-checkbox
                        v-model="blueprint.hasFilterByContractor"
                        :label="$t('[[[Zawęż listę dokumentów na podstawie kontrahenta]]]')"
                    />
                    <ideo-form-group
                        v-if="blueprint.hasFilterByContractor"
                        :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'contractorFilter'))"
                    >
                        <div class="d-flex gap-1 flex-fill">
                            <ideo-select
                                :model-value="contractorFilter.key"
                                :clear-button="false"
                                :deselect="true"
                                :options="contractorComponents"
                                class="w-50"
                                @update:model-value="updateContractorFilter('key', $event)"
                            />
                            <ideo-select
                                v-if="selectedModules.length === 1"
                                :model-value="contractorFilter.value"
                                :clear-button="false"
                                :deselect="true"
                                :options="fieldOptions"
                                class="w-50"
                                @update:model-value="updateContractorFilter('value', $event)"
                            />
                            <ideo-form-input v-else :model-value="$t('[[[Kontrahent]]]')" class="w-50" :disabled="true" />
                        </div>
                    </ideo-form-group>
                    <field-checkbox
                        v-model="blueprint.hasFilterByRelatedDocuments"
                        :label="$t('[[[Zawęź listę dokumentów do dokumentów powiązanych]]]')"
                    />
                    <ideo-form-group
                        v-if="blueprint.hasFilterByRelatedDocuments"
                        :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'relatedDocumentsFilter'))"
                    >
                        <ideo-select
                            :model-value="relatedDocumentsFilter"
                            :options="relatedDocumentComponents"
                            @update:model-value="updateRelatedDocumentsFilter"
                        />
                    </ideo-form-group>
                    <ideo-form-group
                        v-if="selectedModules.length === 1 && blueprint.hasFilterByRelatedDocuments"
                        :invalid-feedback="$t(form.schema.errorMessage(blueprint, 'relatedDocumentsFieldFilter'))"
                    >
                        <template #default>
                            <ideo-select
                                :model-value="relatedDocumentsFieldFilter"
                                :options="relatedDocsFieldOptions"
                                :reload-key="relatedDocumentsFieldReloadKey"
                                :placeholder="$t('[[[Wybierz pole do filtrowania]]]')"
                                @update:model-value="updateRelatedDocumentsFieldFilter"
                            />
                        </template>
                    </ideo-form-group>
                    <field-map
                        v-if="isFilters"
                        v-model="blueprint.filterMappings"
                        :label="$t('[[[Filtry wartościami pól formularza]]]')"
                        :form="form"
                        :blueprint="blueprint"
                        :options="filterOptions"
                        :selected-modules="selectedModules"
                        is-filters
                    />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Mapowanie]]]')">
                    <field-map
                        v-if="isFilters"
                        v-model="blueprint.fieldMappings"
                        :label="$t('[[[Pola mapowane]]]')"
                        :form="form"
                        :blueprint="blueprint"
                        :options="fieldOptions"
                        :selected-modules="selectedModules"
                    />
                    <ideo-form-group
                        v-if="isFilters"
                        :label="$t('[[[Pole do mapowania importu Excel]]]')"
                    >
                        <ideo-select v-model="importFilterField" :options="fieldOptions" />

                        <template #help>
                            {{ $t('[[[Wskaż pole które jest kluczem identyfikującym dokument powiązany, na imporcie excel dokumentu głównego.]]]') }}
                        </template>
                    </ideo-form-group>
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
